// src/pages/Dashboard/Admin/Coaching/utils/coachingDashboardUtils.js

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toNumber(value) {
  const number = Number.parseFloat(value || 0);
  return Number.isFinite(number) ? number : 0;
}

export function formatHours(value) {
  return `${toNumber(value)
    .toLocaleString("fr-FR", {
      maximumFractionDigits: 2,
    })
    .replace(/,00$/, "")} h`;
}

export function formatDateFR(value, options = {}) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options,
  });
}

export function normalizeTeacherEventsResponse(response) {
  if (Array.isArray(response?.Events)) return response.Events;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response)) return response;
  return [];
}

export function getEventCoachHour(event) {
  return toNumber(event?.AdherentEvent?.coach_hour);
}

export function sortEventsByDateDesc(events) {
  return safeArray(events)
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a?.start || 0).getTime();
      const dateB = new Date(b?.start || 0).getTime();

      return dateB - dateA;
    });
}

export function getTeacherFullName(teacher) {
  if (!teacher) return "—";

  return [teacher?.nom, teacher?.prenom].filter(Boolean).join(" ") || "—";
}

export function calculateTeacherStats(teacher, events = []) {
  const rows = safeArray(events);

  const totalHours = rows.reduce(
    (sum, event) => sum + getEventCoachHour(event),
    0
  );

  const eventsWithHours = rows.filter((event) => getEventCoachHour(event) > 0);
  const eventsWithoutHours = rows.filter((event) => getEventCoachHour(event) <= 0);

  const lastEvent = sortEventsByDateDesc(rows)[0] || null;

  return {
    teacherId: teacher?.id || "",
    teacherName: getTeacherFullName(teacher),
    teacher,
    totalEvents: rows.length,
    totalHours: Number(totalHours.toFixed(2)),
    eventsWithHours: eventsWithHours.length,
    eventsWithoutHours: eventsWithoutHours.length,
    lastEventDate: lastEvent?.start || null,
  };
}

export function calculateCoachingGlobalStats(teacherStats = []) {
  const rows = safeArray(teacherStats);

  const totalTeachers = rows.length;
  const totalEvents = rows.reduce((sum, row) => sum + toNumber(row.totalEvents), 0);
  const totalHours = rows.reduce((sum, row) => sum + toNumber(row.totalHours), 0);
  const teachersWithHours = rows.filter((row) => toNumber(row.totalHours) > 0).length;
  const eventsWithoutHours = rows.reduce(
    (sum, row) => sum + toNumber(row.eventsWithoutHours),
    0
  );

  const averageHours =
    totalTeachers > 0 ? Number((totalHours / totalTeachers).toFixed(2)) : 0;

  const topTeacher =
    rows.slice().sort((a, b) => toNumber(b.totalHours) - toNumber(a.totalHours))[0] ||
    null;

  return {
    totalTeachers,
    totalEvents,
    totalHours: Number(totalHours.toFixed(2)),
    teachersWithHours,
    eventsWithoutHours,
    averageHours,
    topTeacher,
  };
}

export function buildChartRowsFromTeacherStats(teacherStats = []) {
  return safeArray(teacherStats)
    .map((row) => ({
      teacherId: row.teacherId,
      nom: row.teacherName,
      hours: Number(toNumber(row.totalHours).toFixed(2)),
      events: row.totalEvents,
    }))
    .sort((a, b) => b.hours - a.hours);
}

export function buildMonthlySummary(events = []) {
  const map = new Map();

  for (const event of safeArray(events)) {
    if (!event?.start) continue;

    const date = new Date(event.start);
    if (Number.isNaN(date.getTime())) continue;

    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}`;

    const previous = map.get(key) || {
      key,
      label: formatMonthLabel(key),
      events: 0,
      hours: 0,
    };

    previous.events += 1;
    previous.hours += getEventCoachHour(event);

    map.set(key, previous);
  }

  return Array.from(map.values())
    .map((row) => ({
      ...row,
      hours: Number(row.hours.toFixed(2)),
    }))
    .sort((a, b) => (a.key < b.key ? -1 : 1));
}

export function formatMonthLabel(yearMonth) {
  const [year, month] = String(yearMonth).split("-").map(Number);

  if (!year || !month) return yearMonth;

  const date = new Date(year, month - 1, 1);

  const label = date.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  return label.charAt(0).toUpperCase() + label.slice(1);
}